import { useMemo, useState } from 'react';
import type { ReactNode, UIEvent } from 'react';

// third party
import { useIntl } from 'react-intl';

// material-ui
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { USER_MORE_ROW, useUserPicker } from '../pages/identity/userSearch';

// ==============================|| USR-COL — SINGLE-SELECT OVER THE USER DIRECTORY ||=================== //
//
// The people twin of `SchoolSelect`, and deliberately a copy of its shape rather than a new idea: same
// props (`value: string`, `onChange(id)`, `''` for nothing chosen, `ariaLabel` for a field with no
// visible label), same clickable footer, same controlled-input rule. A call site converts by swapping the
// component and deleting its 100-user options query.
//
// Why the duplication is right here: the two differ in what a row IS (a school carries a city and a
// salesperson; a person carries a handle) and in the scope parameter (`roleType`), and the generics of a
// shared component would have to abstract both. `useUserPicker` / `useSchoolPicker` already share the
// SHAPE, which is the part that drifts.

export interface UserSelectProps {
  /** Optional: omit inside a layout that already labels the row, or in a table filter cell. */
  label?: string;
  /**
   * ⚠ WITHOUT ONE OF `label` OR THIS, THE FIELD IS ANONYMOUS — an Autocomplete's accessible name comes
   * from its TextField label, so a screen reader announces "combobox" and a test cannot address it.
   */
  ariaLabel?: string;
  /** The stored user id, or '' for "nothing chosen". */
  value: string;
  /** Receives the new id, or '' when the user clears the field. */
  onChange: (id: string) => void;
  /**
   * Narrow the directory to one role type (`supervisor` for a teacher field).
   *
   * ⚠ NOT COSMETIC. The write path refuses a teacher who holds no live supervisor-typed role
   * (`TeacherReference`), so an unscoped picker would offer 127 people of whom 7 are usable.
   */
  roleType?: string;
  /**
   * SP-ROLE: narrow the directory to people who may be CHOSEN AS A SALESPERSON — holders of a role a
   * tenant marked `is_salesperson` in the Roles screen (mig 501).
   *
   * ⚠ NOT A ROLE TYPE, AND NOT INTERCHANGEABLE WITH ONE. Selling is orthogonal to the persona a role
   * routes to: the demo tenant's `sales` role is `administrator`-typed so its holders reach the admin
   * console, and scoping by type instead would have offered every administrator on the tenant.
   *
   * ⚠ A TENANT THAT HAS TICKED NOTHING GETS AN EMPTY PICKER, which is correct and must be SAID —
   * `useUserScopeEmpty` answers that, and a caller passing this should render the sentence rather than
   * letting an empty listbox read as a broken control.
   */
  isSalesperson?: boolean;
  /** APPT-HOST: narrow to holders of one permission code (the appointment host picker). */
  permission?: string;
  /**
   * The name for `value` when the search has not returned it — from `useUserNames`. Without it a saved
   * person renders as a guid until the admin happens to search for them.
   */
  extraNames?: Map<string, string>;
  disabled?: boolean;
  /** Replaces the "15 / 103" line when given; pass nothing to keep the count. */
  helperText?: ReactNode;
  /**
   * TASKS: the chosen person's display name, alongside `onChange`. For a write that stores a NAME
   * SNAPSHOT next to the id (a task's `assigneeName`) — the server cannot read identity [STD-OWN].
   */
  onSelectName?: (name: string) => void;
}

export default function UserSelect({
  label,
  ariaLabel,
  value,
  onChange,
  roleType,
  isSalesperson,
  permission,
  extraNames,
  disabled,
  helperText,
  onSelectName
}: UserSelectProps) {
  const intl = useIntl();
  const selected = useMemo(() => (value ? [value] : []), [value]);
  // ⚠ EVERY SCOPE BELONGS IN THESE DEPS. A prop threaded into the object but left out of the array
  // freezes the picker on whatever scope it first rendered with — it still searches, still pages, and
  // simply answers the wrong question for the rest of the page's life.
  const pickerOptions = useMemo(
    () => ({ roleType, isSalesperson, extraNames, permission }),
    [roleType, isSalesperson, extraNames, permission]
  );
  const picker = useUserPicker(selected, pickerOptions);
  const { search } = picker;

  /**
   * ⚠ A CONTROLLED INPUT MUST STILL SHOW THE SELECTION WHEN IT IS NOT BEING TYPED IN — the rule
   * `SchoolSelect` arrived at, for the same reason: the box is the QUERY, so MUI must not re-seed it from
   * the selected option's label (that wipes the term mid-keystroke), but a single-select whose closed
   * field is blank has lost its own answer.
   *
   * So: the term while focused, the selected person's name otherwise. Focusing CLEARS the term rather
   * than pre-filling it with the label — a search box seeded with a full name matches only that person.
   */
    // ⚠ THE FLAG IS "IS THE USER TYPING A QUERY", NOT "DOES THE FIELD HAVE FOCUS", and conflating
  // the two is what made a chosen option invisible until you clicked away: MUI keeps focus on
  // the input after a selection, so a focus-driven flag stayed true and the box went on showing
  // the stale search term. A selection turns it OFF (the box falls back to the chosen label); a
  // keystroke turns it back ON. onFocus/onBlur still bracket it, because entering the field is
  // the one moment the term should be cleared rather than pre-filled with a full name.
  const [querying, setQuerying] = useState(false);
  const selectedLabel = value ? (picker.nameById.get(value) ?? value) : '';

  return (
    <Autocomplete<string, false>
      fullWidth
      size="small"
      disabled={disabled}
      loading={search.isLoading || search.isTyping}
      options={picker.options}
      // A primitive value needs no memo dance: an id string is referentially stable for free.
      value={value || null}
      onChange={(_e, next) => {
        // The footer row loads the next page instead of choosing a person. It must be CLICKABLE: one
        // page of 15 does not fill the listbox, so a scroll-only "load more" never fires.
        if (next === USER_MORE_ROW) {
          search.loadMore();
          return;
        }
        // ⚠ CHOOSING LEAVES QUERY MODE. MUI keeps focus on the input after a selection, so without
        // this the box would go on showing the search term and the chosen row stayed invisible until
        // the user clicked somewhere else — which is exactly how this was reported.
        setQuerying(false);
        // Name FIRST: a caller that writes on `onChange` reads the name it was just handed.
        onSelectName?.(next ? (picker.nameById.get(next) ?? '') : '');
        onChange(next ?? '');
      }}
      getOptionDisabled={(id) => id === USER_MORE_ROW && !search.hasMore}
      // ⚠ NO CLIENT-SIDE FILTERING — the server already answered this term over the name columns, and
      // MUI's own matcher re-filters by the rendered label, which drops a person found by a name PART
      // that the label does not show.
      filterOptions={(opts) => opts}
      getOptionLabel={(id) => picker.nameById.get(id) ?? id}
      inputValue={querying ? picker.input : selectedLabel}
      onInputChange={(_e, next, reason) => {
        // 'reset' is ignored on purpose — see the note above.
        if (reason === 'input') {
          // Typing re-enters query mode — onFocus cannot, the field is already focused.
          setQuerying(true);
          picker.setInput(next);
        }
        if (reason === 'clear') picker.setInput('');
      }}
      slotProps={{
        listbox: {
          style: { maxHeight: 10 * 36 },
          onScroll: (event: UIEvent<HTMLUListElement>) => {
            const el = event.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) search.loadMore();
          }
        }
      }}
      renderOption={(optionProps, id) => {
        // Keyed by id, not label: display names repeat (two real "Ada Lovelace"s on the demo tenant),
        // and colliding keys leave stale rows in the listbox.
        const { key: _labelKey, ...rest } = optionProps as typeof optionProps & { key?: string };
        if (id === USER_MORE_ROW) {
          return (
            <li {...rest} key={USER_MORE_ROW}>
              <Typography variant="caption" color="text.secondary">
                {picker.footer}
              </Typography>
            </li>
          );
        }
        const name = picker.nameById.get(id) ?? id;
        const detail = picker.detailById?.get(id);
        // ⚠ THE NAME STAYS THE ACCESSIBLE NAME; the code line is its DESCRIPTION. Left as plain content, the
        // caption joins the computed name ("Ada LovelaceTE2701479 · ada@…") — every option query and every
        // screen reader then hears one run-on string instead of a name followed by how to tell it apart.
        const detailId = detail ? `${rest.id ?? id}-detail` : undefined;
        return (
          <li {...rest} key={id} aria-label={name} aria-describedby={detailId}>
            <Stack spacing={0}>
              <span>{name}</span>
              {detail && (
                <Typography id={detailId} variant="caption" color="text.secondary">
                  {detail}
                </Typography>
              )}
            </Stack>
          </li>
        );
      }}
      noOptionsText={intl.formatMessage({ id: 'select-no-options', defaultMessage: 'Sonuç yok' })}
      loadingText={intl.formatMessage({ id: 'select-loading', defaultMessage: 'Yükleniyor…' })}
      renderInput={({ inputProps, ...params }) => (
        <TextField
          {...params}
          label={label}
          onFocus={() => setQuerying(true)}
          onBlur={() => {
            setQuerying(false);
            picker.setInput('');
          }}
          helperText={helperText ?? picker.footer ?? undefined}
          // `inputProps` is pulled out of `params` and re-applied through `slotProps.htmlInput` (its v7
          // name) so the aria-label can be merged in without two competing sources for the same slot.
          slotProps={{ htmlInput: { ...inputProps, ...(ariaLabel ? { 'aria-label': ariaLabel } : {}) } }}
        />
      )}
    />
  );
}
